import {
  FX_BARRICADE,
  FX_DEATH,
  FX_FIRE,
  FX_HIT,
  FX_REACH,
  FX_SLOW,
  FX_TOWER_DOWN,
  fxAt,
  fxCount,
  type Frame,
} from "@empire/core-sim";

/**
 * Turning a battle into movement (S4-2, D-046).
 *
 * Hit, frost, death, recoil and projectiles remain engine effects (D-046).
 * D-121 adds authored walk poses in motion.ts; both layers use replay time.
 *
 * Everything here is a pure function of (frames, position). That is not tidiness
 * for its own sake: the replay has a scrubber, so the same moment has to look
 * the same whether you arrived at it by watching, by dragging backwards, or by
 * pausing and coming back tomorrow. An animation that carried its own clock
 * would drift the moment somebody touched the slider, and the first thing a
 * tester does with a scrubber is drag it.
 */

/** Ticks a projectile is in the air. It LANDS on the tick the shot resolves. */
export const FLIGHT_TICKS = 2;
/**
 * The durations below are in TICKS of replay (0.1 s each at 1×). They were
 * first set at two to four ticks — true to the simulation, and invisible on a
 * phone: a 0.2 s flash on a five-pixel dot is not an animation anybody sees.
 * Playtest readiness (Sep 2026) roughly doubled them. They are pure display:
 * nothing here feeds back into the battle.
 */
/** Ticks a tower stays pushed back after firing. */
export const RECOIL_TICKS = 3;
/** Ticks a hit unit stays squashed and pale. */
export const HIT_TICKS = 4;
/** Ticks a dying unit takes to fade out — long enough to see it fall. */
export const FADE_TICKS = 8;
/** Ticks the frost tint lingers after a slow lands. */
export const FROST_TICKS = 12;
/** Ticks the impact burst of a landed shot takes to expand and vanish. */
export const BURST_TICKS = 5;
/** Ticks a damage number floats up before it is gone. */
export const POP_TICKS = 9;
/** Ticks a barricade takes to collapse, and a silenced tower to puff smoke. */
export const COLLAPSE_TICKS = 8;

/** How far back any effect can reach. The scan window has to cover all of them. */
const LOOKBACK_TICKS = Math.max(FROST_TICKS, FADE_TICKS, POP_TICKS, BURST_TICKS, COLLAPSE_TICKS);

/** A landed shot, drawn as a ring (and debris) where it hit. */
export interface Burst {
  /** Tile index of the tower that fired, or -1 when the viewer never saw it. */
  readonly tile: number;
  readonly x: number;
  readonly y: number;
  /** 0..1 through the burst. */
  readonly t: number;
}

/** A floating damage number. */
export interface Pop {
  readonly unitId: number;
  readonly x: number;
  readonly y: number;
  readonly amount: number;
  /** 0..1 through its rise. */
  readonly t: number;
}

export interface Gait {
  /** Screen pixels up, as the unit bobs. */
  readonly bob: number;
  /** Radians, alternating — the lean of something walking with weight. */
  readonly tilt: number;
  /** 1 = its own size. Circles have no visible tilt, so they breathe instead. */
  readonly squash: number;
}

/** What a unit looks like right now, beyond where it is. */
export interface UnitFx {
  /** 0..1, decaying: how hard it was just hit. */
  readonly impact: number;
  /** 0..1: how far into dying it is. 1 means gone. */
  readonly fade: number;
  /** 0..1, decaying: how recently Frost caught it. */
  readonly frost: number;
  /** True when it got in rather than died — drawn as a rise, not a collapse. */
  readonly reached: boolean;
}

/** A projectile in the air, in milli-tiles. */
export interface Shot {
  /** Tile index of the tower that fired. */
  readonly tile: number;
  /** Index into TOWER_KINDS, for the colour. -1 when unknown. */
  readonly kind: number;
  readonly fromX: number;
  readonly fromY: number;
  readonly toX: number;
  readonly toY: number;
  /** 0..1 along the flight. */
  readonly t: number;
}

export interface FxState {
  readonly units: Map<number, UnitFx>;
  /** Tile index -> 0..1 recoil, strongest at the moment of firing. */
  readonly towers: Map<number, number>;
  readonly shots: Shot[];
  /** Units that have left the board but are still fading, in milli-tiles. */
  readonly ghosts: Map<number, { x: number; y: number }>;
  readonly bursts: Burst[];
  readonly pops: Pop[];
  /**
   * Barricades that have fallen by now: tile -> 0..1 through the collapse
   * (1 = rubble). Scanned from the START of the battle, not a window, because a
   * barricade that fell forty seconds ago is still gone.
   */
  readonly fallen: Map<number, number>;
  /** Towers knocked offline by now: tile -> ticks since it happened. */
  readonly silenced: Map<number, number>;
  /** Unit id -> health in thousandths, at this moment. Absent = full / unknown. */
  readonly hp: Map<number, number>;
}

/**
 * The walk cycle.
 *
 * Driven by the replay position rather than by wall-clock time, so at 4× speed
 * the legs move four times as fast — which is what makes fast-forward read as
 * fast-forward rather than as a slideshow. The per-unit offset stops forty
 * units from bobbing in lockstep like a chorus line.
 */
export function gait(unitId: number, position: number, moving: boolean): Gait {
  if (!moving) return { bob: 0, tilt: 0, squash: 1 };
  const phase = position * 0.9 + unitId * 1.7;
  return {
    bob: Math.abs(Math.sin(phase)) * -2.2,
    tilt: Math.sin(phase * 0.5) * 0.14,
    squash: 1 + Math.sin(phase) * 0.06,
  };
}

/**
 * Everything the board needs to draw one moment of a battle.
 *
 * Scans a window of frames around `position` rather than keeping state between
 * calls — a few dozen frames of flat integers, which is cheaper than it sounds
 * and is the reason scrubbing backwards works at all.
 */
export function fxState(frames: readonly Frame[], position: number): FxState {
  const units = new Map<number, UnitFx>();
  const towers = new Map<number, number>();
  const shots: Shot[] = [];
  const ghosts = new Map<number, { x: number; y: number }>();
  const bursts: Burst[] = [];
  const pops: Pop[] = [];
  const fallen = new Map<number, number>();
  const silenced = new Map<number, number>();
  const hp = new Map<number, number>();
  if (frames.length === 0) return { units, towers, shots, ghosts, bursts, pops, fallen, silenced, hp };

  const now = Math.max(0, Math.min(position, frames.length - 1));
  const back = Math.max(0, Math.floor(now - LOOKBACK_TICKS));

  // Health: read off the nearest frame at or before now. Bars do not need
  // interpolating — they move in steps because damage does.
  const hpFrame = frames[Math.floor(now)];
  if (hpFrame?.hp && hpFrame.hp.length > 0) {
    for (let i = 0, k = 0; i + 2 < hpFrame.units.length; i += 3, k++) {
      const value = hpFrame.hp[k];
      if (value !== undefined && value < 1000) hp.set(hpFrame.units[i]!, value);
    }
  }

  // What has been knocked down stays down: scan from the start for those two.
  for (let f = 0; f <= Math.floor(now); f++) {
    const frame = frames[f];
    if (!frame) continue;
    for (let i = 0; i < fxCount(frame.fx); i++) {
      const { code, a } = fxAt(frame.fx, i);
      if (a < 0) continue;
      if (code === FX_BARRICADE && !fallen.has(a)) {
        fallen.set(a, Math.min(1, (now - f) / COLLAPSE_TICKS));
      } else if (code === FX_TOWER_DOWN && !silenced.has(a)) {
        silenced.set(a, now - f);
      }
    }
  }
  // Look ahead as well: a shot has to leave the tower BEFORE it lands, and the
  // simulation resolves both in the same tick. Drawing the flight in the ticks
  // leading up to the hit keeps the impact honest and only the launch early.
  const ahead = Math.min(frames.length - 1, Math.ceil(now + FLIGHT_TICKS));

  const impacts = new Map<number, number>();
  const frosts = new Map<number, number>();
  const deaths = new Map<number, { tick: number; reached: boolean }>();

  for (let f = back; f <= ahead; f++) {
    const frame = frames[f];
    if (!frame) continue;
    const age = now - f; // negative means it has not happened yet

    for (let i = 0; i < fxCount(frame.fx); i++) {
      const { code, a, b, c } = fxAt(frame.fx, i);

      if (code === FX_FIRE) {
        // The shot lands at this frame's tick, so it is in the air during the
        // FLIGHT_TICKS before it.
        const t = (now - (f - FLIGHT_TICKS)) / FLIGHT_TICKS;
        if (t >= 0 && t <= 1 && a >= 0) {
          const target = unitPositionAt(frames, f, b) ?? unitPositionAt(frames, f - 1, b);
          if (target) {
            shots.push({ tile: a, kind: c, fromX: -1, fromY: -1, toX: target.x, toY: target.y, t });
          }
        }
        // Recoil starts when the shot leaves, not when it lands.
        const recoilAge = now - (f - FLIGHT_TICKS);
        if (recoilAge >= 0 && recoilAge <= RECOIL_TICKS && a >= 0) {
          const strength = 1 - recoilAge / RECOIL_TICKS;
          towers.set(a, Math.max(towers.get(a) ?? 0, strength));
        }
        continue;
      }

      if (age < 0) continue; // everything else is only drawn once it has happened

      if (code === FX_HIT) {
        if (age <= HIT_TICKS) impacts.set(a, Math.max(impacts.get(a) ?? 0, 1 - age / HIT_TICKS));
        if (age <= BURST_TICKS || age <= POP_TICKS) {
          const at = unitPositionAt(frames, f, a) ?? unitPositionAt(frames, f - 1, a);
          if (at) {
            if (age <= BURST_TICKS) bursts.push({ tile: b, x: at.x, y: at.y, t: age / BURST_TICKS });
            if (age <= POP_TICKS && c > 0) {
              pops.push({ unitId: a, x: at.x, y: at.y, amount: c, t: age / POP_TICKS });
            }
          }
        }
      } else if (code === FX_SLOW && age <= FROST_TICKS) {
        frosts.set(a, Math.max(frosts.get(a) ?? 0, 1 - age / FROST_TICKS));
      } else if (code === FX_DEATH && age <= FADE_TICKS) {
        deaths.set(a, { tick: f, reached: false });
      } else if (code === FX_REACH && age <= FADE_TICKS) {
        deaths.set(a, { tick: f, reached: true });
      }
    }
  }

  for (const [id, { tick, reached }] of deaths) {
    const at = unitPositionAt(frames, tick, id) ?? unitPositionAt(frames, tick - 1, id);
    if (at) ghosts.set(id, at);
    units.set(id, {
      impact: impacts.get(id) ?? 0,
      fade: Math.min(1, Math.max(0, (now - tick) / FADE_TICKS)),
      frost: frosts.get(id) ?? 0,
      reached,
    });
  }
  for (const [id, impact] of impacts) {
    if (units.has(id)) continue;
    units.set(id, { impact, fade: 0, frost: frosts.get(id) ?? 0, reached: false });
  }
  for (const [id, frost] of frosts) {
    if (units.has(id)) continue;
    units.set(id, { impact: 0, fade: 0, frost, reached: false });
  }

  return { units, towers, shots, ghosts, bursts, pops, fallen, silenced, hp };
}

/** Where a unit was at a given frame, in milli-tiles, or null if it was not there. */
export function unitPositionAt(
  frames: readonly Frame[],
  frameIndex: number,
  unitId: number
): { x: number; y: number } | null {
  const frame = frames[frameIndex];
  if (!frame) return null;
  for (let i = 0; i + 2 < frame.units.length; i += 3) {
    if (frame.units[i] === unitId) return { x: frame.units[i + 1]!, y: frame.units[i + 2]! };
  }
  return null;
}

/**
 * Positions at a fractional moment, interpolated between the two ticks either
 * side of it.
 *
 * The simulation runs at 10 ticks a second and the screen draws at 60. Without
 * this, everything on the board moves in ten jumps a second, which is the
 * single biggest reason the replay looked like a diagram rather than a battle.
 * A unit that appears in only one of the two frames is not interpolated — it
 * either just spawned or just left, and inventing a position for it would draw
 * it sliding in from wherever it happened to be standing.
 */
export function interpolate(frames: readonly Frame[], position: number): number[] {
  if (frames.length === 0) return [];
  const clamped = Math.max(0, Math.min(position, frames.length - 1));
  const i = Math.floor(clamped);
  const t = clamped - i;
  const a = frames[i];
  const b = frames[i + 1];
  if (!a) return [];
  if (!b || t === 0) return [...a.units];

  const out: number[] = [];
  for (let k = 0; k + 2 < a.units.length; k += 3) {
    const id = a.units[k]!;
    const ax = a.units[k + 1]!;
    const ay = a.units[k + 2]!;
    const next = unitPositionAt(frames, i + 1, id);
    if (!next) {
      out.push(id, ax, ay);
      continue;
    }
    out.push(id, Math.round(ax + (next.x - ax) * t), Math.round(ay + (next.y - ay) * t));
  }
  return out;
}

// ===========================================================================
// Tapping something while the replay is paused (S4-5).
// ===========================================================================

/**
 * What a unit or a tower has done, up to this moment and no further.
 *
 * "So far" is the whole point. Pausing at eight seconds and being told the
 * total for the whole battle would answer a question nobody asked — the reason
 * to pause is to find out what had happened BY then, usually because something
 * has just gone wrong and you want to know which tower was doing the work.
 *
 * Everything is read back out of the same fx stream the animation uses, so a
 * fogged attacker's popover is fogged for free: a tower they never earned is
 * not in their stream, so it cannot be tapped and its hits are attributed to
 * nobody.
 */
export interface UnitReport {
  readonly kind: "unit";
  readonly id: number;
  readonly damageTaken: number;
  readonly timesHit: number;
  /** Ticks of frost still on it, roughly — 0 when it was never slowed. */
  readonly slowed: boolean;
  readonly status: "walking" | "dead" | "reached";
}

export interface TowerReport {
  readonly kind: "tower";
  /** Board tile index. */
  readonly tile: number;
  readonly shots: number;
  readonly damageDealt: number;
  readonly kills: number;
}

export type Report = UnitReport | TowerReport;

/** What this unit has been through by `position`. */
export function inspectUnit(
  frames: readonly Frame[],
  position: number,
  unitId: number
): UnitReport {
  let damageTaken = 0;
  let timesHit = 0;
  let slowed = false;
  let status: UnitReport["status"] = "walking";

  const now = Math.max(0, Math.min(position, frames.length - 1));
  for (let f = 0; f <= now; f++) {
    const frame = frames[f];
    if (!frame) continue;
    for (let i = 0; i < fxCount(frame.fx); i++) {
      const { code, a, c } = fxAt(frame.fx, i);
      if (a !== unitId) continue;
      if (code === FX_HIT) {
        damageTaken += c;
        timesHit += 1;
      } else if (code === FX_SLOW) {
        slowed = true;
      } else if (code === FX_DEATH) {
        status = "dead";
      } else if (code === FX_REACH) {
        status = "reached";
      }
    }
  }

  return { kind: "unit", id: unitId, damageTaken, timesHit, slowed, status };
}

/** What this tower has done by `position`. */
export function inspectTower(
  frames: readonly Frame[],
  position: number,
  tile: number
): TowerReport {
  let shots = 0;
  let damageDealt = 0;
  let kills = 0;

  const now = Math.max(0, Math.min(position, frames.length - 1));
  for (let f = 0; f <= now; f++) {
    const frame = frames[f];
    if (!frame) continue;
    for (let i = 0; i < fxCount(frame.fx); i++) {
      const { code, a, b, c } = fxAt(frame.fx, i);
      if (code === FX_FIRE && a === tile) shots += 1;
      else if (code === FX_HIT && b === tile) damageDealt += c;
      else if (code === FX_DEATH && b === tile) kills += 1;
    }
  }

  return { kind: "tower", tile, shots, damageDealt, kills };
}
